import { toWireDate, type ParkedSummary, type ParkedWorklog } from '@forecastify/shared';
import { Badge, Button, DataList, Icon, type DataListRow } from '@forecastify/ui';
import { useDuration } from '../../duration';
import type { MessageKey } from '../../i18n/catalogue';
import type { Translate } from '../../i18n';
import { listLabels } from '../delivery/clients-screen';
import { useCalendarLabels } from '../date-field';
import type { Pages } from '../use-pages';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C36 · the parked-hours inbox.                                             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS SCREEN IS THE ONLY PLACE AN HOUR EXISTS AND BELONGS TO NOBODY**, and it exists
 * because `worklog/updated?since=` is forward-only (`jira.md` §1.4): an hour we cannot file is one
 * we never see again. Everywhere else a worklog has a task and a person because both columns are
 * `not null`; here it has neither yet.
 *
 * ⚠️ **THE TWO CAUSES GET DIFFERENT WORDS, BECAUSE THEY NEED DIFFERENT PEOPLE.** `no_member` is
 * fixed in People management by somebody who knows who these colleagues are; `no_task` is fixed on
 * the links tab by somebody who knows which projects are ours.
 *
 * ⚠️⚠️ 17 Oct 2026 · **A TABLE SOMEBODY CAN SEARCH, FILTER AND PAGE — the product owner.** It was
 * a checkbox list with a pager. Now the kit's `DataList`, every narrowing done by the SERVER (the
 * inbox outgrows a page within a week): a search over the issue key, the author and the note, and
 * filters by date, by who logged it, by that person's manager and by the reason. The issue column
 * shows `ABC-123` (`0221`), falling back to Jira's numeric id until the sync has named it.
 *
 * ⚠️ **The manager filter can only match people we have PAIRED with a member** — an unpaired Jira
 * account has no member and so no manager. Its options say as much by being only the managers
 * that exist.
 */

const REASON: Record<ParkedWorklog['reason'], MessageKey> = {
  no_task: 'integrations.parked.reason.noTask',
  no_member: 'integrations.parked.reason.noMember',
};
const FIX: Record<ParkedWorklog['reason'], MessageKey> = {
  no_task: 'integrations.parked.fix.noTask',
  no_member: 'integrations.parked.fix.noMember',
};

/** What the table narrows by — every field `''` when unset, as the toolbar holds them. */
export interface ParkedQuery {
  readonly search: string;
  /** `from..to`, the toolbar's range value. */
  readonly dates: string;
  readonly authorId: string;
  readonly managerId: string;
  readonly reason: string;
}

export const EMPTY_PARKED_QUERY: ParkedQuery = {
  search: '',
  dates: '',
  authorId: '',
  managerId: '',
  reason: '',
};

/** The query as request parameters — the one place the range is split. */
export function parkedParams(query: ParkedQuery, params: URLSearchParams): void {
  if (query.search.trim() !== '') params.set('search', query.search.trim());
  const [from = '', to = ''] = query.dates.split('..');
  if (from !== '') params.set('from', from);
  if (to !== '') params.set('to', to);
  if (query.authorId !== '') params.set('authorId', query.authorId);
  if (query.managerId !== '') params.set('managerId', query.managerId);
  if (query.reason !== '') params.set('reason', query.reason);
}

export function ParkedTab({
  t,
  rows,
  summary,
  pages,
  busy,
  query,
  onQuery,
  onRun,
  onDiscard,
  onLinks,
  onPeople,
}: {
  readonly t: Translate;
  /** ⚠️ Null until it lands — *"nothing waiting"* and *"not read yet"* are different facts. */
  readonly rows: readonly ParkedWorklog[] | null;
  /**
   * ⚠️⚠️ **J-4 · THE TOTALS, OVER EVERY ROW — NEVER THE PAGE'S SUM.** This tab used to add up the
   * 200 rows it held and call it the inbox. Null until it lands.
   */
  readonly summary: ParkedSummary | null;
  readonly pages: Pages;
  readonly busy: boolean;
  readonly query: ParkedQuery;
  readonly onQuery: (next: ParkedQuery) => void;
  readonly onRun: () => void;
  readonly onDiscard: (ids: readonly string[]) => void;
  readonly onLinks: () => void;
  readonly onPeople: () => void;
}) {
  const calendar = useCalendarLabels();
  /* 3 Nov 2026 · the reader's unit (`decisions.md` DU-1). */
  const { fmt } = useDuration();
  const waiting = rows ?? [];
  const set = (patch: Partial<ParkedQuery>): void => {
    onQuery({ ...query, ...patch });
  };

  const listed: DataListRow[] = waiting.map((row) => ({
    id: row.id,
    cells: {
      date: { text: row.workedOn, mono: true, muted: true },
      hours: { text: fmt(row.hours), mono: true, bold: true },
      issue:
        row.remoteIssueKey === null
          ? { text: `#${row.remoteIssueId}`, mono: true, muted: true }
          : { text: row.remoteIssueKey, mono: true },
      /* ⚠️ The person's name if we have it, and the raw account id if we do not — which is itself
         the diagnosis for `no_member`. */
      author: {
        text: row.remoteAuthorName ?? row.remoteAuthorId ?? t('integrations.parked.authorUnknown'),
        muted: row.remoteAuthorName === null,
      },
      /**
       * ⚠️ **`outline`, not `destructive`, and `runBadge`'s `partial` is the precedent.** A parked
       * hour is waiting, not broken — and `no_member` is the COMMON case.
       */
      reason: { badge: { label: t(REASON[row.reason]), variant: 'outline' as const } },
      note: { text: row.note === null ? '' : stripTags(row.note), muted: true },
    },
  }));

  return (
    <div className="flex flex-col gap-4" data-testid="integration-parked">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-[13px] font-medium">{t('integrations.parked.heading')}</h2>
          <p className="text-muted-foreground text-[11.5px]">
            {/* ⚠️ The TOTAL, not the count. Twelve rows is a list; nineteen hours is a number
                somebody recognises as missing from a month. */}
            {summary === null
              ? null
              : summary.count === 0
                ? t('integrations.parked.none')
                : t('integrations.parked.waiting', {
                    count: summary.count,
                    hours: fmt(summary.hours),
                  })}
          </p>
        </div>
        <Button variant="outline" size="sm" disabled={busy} onClick={onRun}>
          <Icon name="refresh-cw" size={13} />
          {t('integrations.syncRecent')}
        </Button>
      </div>

      {/* ⚠️ J-4 · WHOSE hours, over the whole inbox — *"why are Gábor's missing?"* is answered here
          rather than by paging through five hundred rows. */}
      {summary === null || summary.authors.length === 0 ? null : (
        <div className="flex flex-col gap-1.5">
          <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
            {t('integrations.parked.byAuthor')}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {summary.authors.map((author) => (
              <Badge key={author.remoteAuthorId ?? ''} variant="outline">
                {author.remoteAuthorName ??
                  author.remoteAuthorId ??
                  t('integrations.parked.authorUnknown')}
                <span className="text-muted-foreground ml-1.5 tabular-nums">
                  {fmt(author.hours)}
                </span>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* ⚠️ The fix, named, next to the count that needs it — from the totals, so a reason that
          waits only on a later page still gets its button. */}
      {summary === null || summary.count === 0 ? null : (
        <div className="flex flex-wrap items-center gap-2">
          {(['no_member', 'no_task'] as const)
            .filter((reason) =>
              summary.reasons.some((one) => one.reason === reason && one.count > 0),
            )
            .map((reason) => (
              <Button
                key={reason}
                variant="ghost"
                size="sm"
                onClick={reason === 'no_member' ? onPeople : onLinks}
              >
                <Icon name={reason === 'no_member' ? 'user-round' : 'link'} size={13} />
                {t(FIX[reason])}
              </Button>
            ))}
        </div>
      )}

      {rows === null ? null : (
        <div className="overflow-hidden rounded-lg border">
          <DataList
            columnsKey="parked-hours"
            card={{
              title: 'issue',
              context: 'date',
              defaults: { avatar: 'author', details: ['hours', 'reason', 'note'] },
            }}
            columns={[
              { key: 'date', label: t('time.column.date'), width: 100 },
              { key: 'hours', label: t('common.hours'), width: 72, align: 'right' },
              { key: 'issue', label: t('integrations.parked.col.issue'), width: 120 },
              { key: 'author', label: t('integrations.parked.col.author'), width: 200 },
              { key: 'reason', label: t('integrations.parked.col.reason'), width: 170 },
              { key: 'note', label: t('integrations.parked.col.note') },
            ]}
            rows={listed}
            page={{
              hasPrevious: pages.hasPrevious,
              hasNext: pages.hasNext,
              onPrevious: pages.previous,
              onNext: pages.next,
              index: pages.index,
              count: pages.count,
              total: pages.total,
              onGoTo: pages.goTo,
              pageSize: pages.limit,
            }}
            search={query.search}
            onSearchChange={(value) => {
              set({ search: value });
            }}
            filters={[
              {
                key: 'dates',
                label: t('time.column.date'),
                value: query.dates,
                kind: 'range',
                input: 'date',
                calendar,
                today: toWireDate(new Date()),
                options: [],
                display: (value) => value.replace('..', ' – '),
                onChange: (value) => {
                  set({ dates: value });
                },
              },
              {
                key: 'author',
                kind: 'multi' as const,
                label: t('integrations.parked.col.author'),
                value: query.authorId,
                onChange: (value) => {
                  set({ authorId: value });
                },
                options: (summary?.authorOptions ?? []).map((author) => ({
                  value: author.remoteAuthorId,
                  label: author.remoteAuthorName ?? author.remoteAuthorId,
                })),
              },
              {
                key: 'manager',
                kind: 'multi' as const,
                label: t('people.field.manager'),
                value: query.managerId,
                onChange: (value) => {
                  set({ managerId: value });
                },
                options: (summary?.managerOptions ?? []).map((manager) => ({
                  value: manager.memberId,
                  label: manager.name,
                })),
              },
              {
                key: 'reason',
                kind: 'multi' as const,
                label: t('integrations.parked.col.reason'),
                value: query.reason,
                onChange: (value) => {
                  set({ reason: value });
                },
                options: (['no_member', 'no_task'] as const).map((reason) => ({
                  value: reason,
                  label: t(REASON[reason]),
                })),
              },
            ]}
            /* ⚠️⚠️ **Discarding LOSES the hour, and the action says so.** The feed will never
                offer it again unless the same window re-reads it. */
            bulkActions={[
              {
                label: t('integrations.parked.discardSelected'),
                icon: 'trash',
                destructive: true,
                onSelect: (ids) => {
                  if (!busy) onDiscard(ids);
                },
              },
            ]}
            empty={{
              icon: 'clock',
              title:
                summary !== null && summary.count > 0
                  ? t('integrations.parked.noMatch')
                  : t('integrations.parked.emptyBody'),
              body: '',
            }}
            labels={{ ...listLabels(t), search: t('integrations.parked.search') }}
          />
        </div>
      )}
    </div>
  );
}

/** A note arrives as sanitised HTML from the tracker; the table shows its words. */
function stripTags(html: string): string {
  return html
    .replace(/<[^>]*>/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim();
}
